import { useMemo, useState } from 'react';
import {
  Box, Button, Flex, HStack, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, SimpleGrid, Text, useDisclosure,
} from '@chakra-ui/react';
import { PresetDoc, useListPresetsQuery } from '../redux/services/mixgenie';

/**
 * Modeled after the pictools filter grid: a modal (or inline sheet) full of
 * image-forward tiles the user can tap to add a preset. Tiles show the
 * emoji-on-gradient (or `imageUrl` if uploaded) with the name overlaid on a
 * dark scrim. Selected tiles get a pink outline and a check corner.
 *
 * The picker is filtered by `target` — same modal, different data — so the
 * generator page can render "+ Add sound preset" and "+ Add lyric theme"
 * buttons that open the same component.
 */
export function PresetPicker({
  target,
  selectedIds,
  onApply,
  triggerLabel,
}: {
  target: 'sound' | 'lyrics';
  selectedIds: string[];
  onApply: (nextSelected: PresetDoc[]) => void;
  triggerLabel: string;
}) {
  const modal = useDisclosure();
  const { data, isLoading } = useListPresetsQuery();
  const groups = useMemo(() => (
    (data?.groups || []).filter((g) => g.target === target)
  ), [data, target]);

  // Working copy — mutated in-modal so cancel doesn't apply changes.
  const [draft, setDraft] = useState<Set<string>>(new Set());

  const open = () => {
    setDraft(new Set(selectedIds));
    modal.onOpen();
  };

  const toggle = (id: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const apply = () => {
    const flat = groups.flatMap((g) => g.presets);
    const chosen = flat.filter((p) => draft.has(String(p._id)));
    onApply(chosen);
    modal.onClose();
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        colorScheme="pink"
        borderStyle="dashed"
        onClick={open}
        borderRadius="full"
      >
        + {triggerLabel}
      </Button>
      <Modal isOpen={modal.isOpen} onClose={modal.onClose} size={{ base: 'full', md: '2xl' }} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent bg="night.900" color="whiteAlpha.900" borderRadius={{ base: 0, md: '16px' }}>
          <ModalHeader>{triggerLabel}</ModalHeader>
          <ModalCloseButton />
          <ModalBody px={{ base: 3, md: 6 }}>
            {isLoading && <Text color="whiteAlpha.600">Loading…</Text>}
            {!isLoading && groups.length === 0 && (
              <Text color="whiteAlpha.600">No presets available yet.</Text>
            )}
            {groups.map((g) => (
              <Box key={g.category} mb={6}>
                <Text
                  fontSize="xs"
                  fontWeight={700}
                  color="whiteAlpha.600"
                  mb={3}
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  {g.category}
                </Text>
                <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={3}>
                  {g.presets.map((p) => (
                    <PresetTile
                      key={String(p._id)}
                      preset={p}
                      selected={draft.has(String(p._id))}
                      onClick={() => toggle(String(p._id))}
                    />
                  ))}
                </SimpleGrid>
              </Box>
            ))}
          </ModalBody>
          <ModalFooter borderTopWidth="1px" borderColor="whiteAlpha.200">
            <HStack width="full" justify="space-between">
              <Text fontSize="sm" color="whiteAlpha.600">
                {draft.size} selected
              </Text>
              <HStack>
                <Button variant="ghost" colorScheme="whiteAlpha" onClick={modal.onClose}>Cancel</Button>
                <Button colorScheme="pink" onClick={apply}>Apply</Button>
              </HStack>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

function PresetTile({
  preset,
  selected,
  onClick,
}: {
  preset: PresetDoc;
  selected: boolean;
  onClick: () => void;
}) {
  const gradient = presetGradient(preset.gradient);
  return (
    <Box
      as="button"
      onClick={onClick}
      position="relative"
      aspectRatio={1}
      borderRadius="14px"
      overflow="hidden"
      borderWidth="2px"
      borderColor={selected ? 'pink.400' : 'transparent'}
      transform={selected ? 'scale(0.97)' : 'scale(1)'}
      transition="border-color 0.15s, transform 0.15s"
      _hover={{ borderColor: selected ? 'pink.400' : 'whiteAlpha.500' }}
      _focus={{ outline: '2px solid', outlineColor: 'pink.400' }}
      textAlign="left"
    >
      {preset.imageUrl ? (
        // eslint-disable-next-line jsx-a11y/alt-text
        <Box
          position="absolute"
          inset={0}
          bgImage={`url(${preset.imageUrl})`}
          bgSize="cover"
          bgPosition="center"
        />
      ) : (
        <Flex position="absolute" inset={0} bgGradient={gradient} align="center" justify="center">
          <Text fontSize="4xl" lineHeight={1} aria-hidden>{preset.emoji || '🎵'}</Text>
        </Flex>
      )}
      {/* Bottom scrim + label */}
      <Box
        position="absolute"
        left={0}
        right={0}
        bottom={0}
        px={2}
        py={2}
        bgGradient="linear(to-t, blackAlpha.800, transparent)"
      >
        <Text color="white" fontSize="sm" fontWeight={700} noOfLines={2}>
          {preset.name}
        </Text>
      </Box>
      {selected && (
        <Flex
          position="absolute"
          top={2}
          right={2}
          boxSize="24px"
          borderRadius="full"
          bg="pink.500"
          color="white"
          align="center"
          justify="center"
          fontSize="sm"
          fontWeight={700}
        >
          ✓
        </Flex>
      )}
    </Box>
  );
}

/**
 * `gradient` is stored as two comma-separated Chakra color tokens (e.g.
 * "pink.500,purple.600"). We translate to a 135° `bgGradient` string. Any
 * malformed value falls back to the brand pink→purple ramp so the picker
 * never renders empty tiles.
 */
export function presetGradient(spec: string | undefined | null): string {
  if (!spec) return 'linear(135deg, pink.500, purple.600)';
  const parts = spec.split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length < 2) return 'linear(135deg, pink.500, purple.600)';
  return `linear(135deg, ${parts[0]}, ${parts[1]})`;
}
